import * as yup from 'yup';

import { requiredString } from 'utils/constants';

export const FormFields = {
  patientAccountNumber: 'patientAccountNumber',
  chiefComplaint: 'chiefComplaint',
  modeOfArrivalText: 'modeOfArrivalText',
  admitTimestamp: 'admitTimestamp',
  dischargedDisposition: 'dischargedDisposition',
  dischargeTimestamp: 'dischargeTimestamp',
  dischargeLocation: 'dischargeLocation',
  billToProviderLastname: 'billToProviderLastname',
  billToProviderFirstname: 'billToProviderFirstname',
  billtToProviderMiddlename: 'billtToProviderMiddlename',
  billToProviderId: 'billToProviderId',
  patientLocation: 'patientLocation',
  patientClass: 'patientClass',
  hospitalService: 'hospitalService',
  patientType: 'patientType',
  servicingFacility: 'servicingFacility',
  accountStatus: 'accountStatus',
  hasInsurance: 'hasInsurance',
};

export const schema = yup.object().shape({
  [FormFields.patientAccountNumber]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `Patient Account Number should not exceed ${max} characters`),
  [FormFields.dischargeTimestamp]: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Invalid discharge date'),
  [FormFields.chiefComplaint]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Chief Complaint should not exceed ${max} characters`),
  [FormFields.modeOfArrivalText]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Mode of Arrival should not exceed ${max} characters`),
  [FormFields.admitTimestamp]: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Invalid admit date'),
  [FormFields.dischargedDisposition]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Discharged Disposition should not exceed ${max} characters`),
  [FormFields.dischargeLocation]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Discharge Location should not exceed ${max} characters`),
  [FormFields.billToProviderLastname]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Provider Last Name should not exceed ${max} characters`),
  [FormFields.billToProviderFirstname]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Provider First Name should not exceed ${max} characters`),
  [FormFields.billtToProviderMiddlename]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Provider Middle Name should not exceed ${max} characters`),
  [FormFields.billToProviderId]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Provider ID should not exceed ${max} characters`),
  [FormFields.patientLocation]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Patient Location should not exceed ${max} characters`),
  [FormFields.patientClass]: yup
    .string()
    .trim()
    .max(50, ({ max }) => `Patient Class should not exceed ${max} characters`),
  [FormFields.hospitalService]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Hospital Service should not exceed ${max} characters`),
  [FormFields.patientType]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Patient Type should not exceed ${max} characters`),
  [FormFields.servicingFacility]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Servicing Facility should not exceed ${max} characters`),
  [FormFields.accountStatus]: yup
    .string()
    .trim()
    .max(50, ({ max }) => `Account Status should not exceed ${max} characters`),
});

export const mapApi2Form = (apiData) => {
  if (!apiData || Object.keys(apiData).length === 0) {
    return {
      [FormFields.hasInsurance]: 'No',
    };
  }

  return {
    id: apiData.id,
    [FormFields.patientAccountNumber]: apiData.patientAccountNumber || '',
    [FormFields.chiefComplaint]: apiData.chiefComplaint || '',
    [FormFields.modeOfArrivalText]: apiData.modeOfArrivalText || '',
    [FormFields.admitTimestamp]: apiData.admitTimestamp || null,
    [FormFields.dischargedDisposition]: apiData.dischargedDisposition || '',
    [FormFields.dischargeTimestamp]: apiData.dischargeTimestamp || null,
    [FormFields.dischargeLocation]: apiData.dischargeLocation || '',
    [FormFields.billToProviderLastname]: apiData.billToProviderLastname || '',
    [FormFields.billToProviderFirstname]: apiData.billToProviderFirstname || '',
    [FormFields.billtToProviderMiddlename]: apiData.billtToProviderMiddlename || '',
    [FormFields.billToProviderId]: apiData.billToProviderId || '',
    [FormFields.patientLocation]: apiData.patientLocation || '',
    [FormFields.patientClass]: apiData.patientClass || '',
    [FormFields.hospitalService]: apiData.hospitalService || '',
    [FormFields.patientType]: apiData.patientType || '',
    [FormFields.servicingFacility]: apiData.servicingFacility || '',
    [FormFields.accountStatus]: apiData.accountStatus || '',
    [FormFields.hasInsurance]: apiData.hasInsurance ? 'Yes' : 'No',
  };
};

export const mapForm2Api = (formData) => {
  const payload = {};

  const addIfNotEmpty = (key, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (trimmedValue !== null && trimmedValue !== undefined && trimmedValue !== '') {
      payload[key] = trimmedValue;
    }
  };

  if (formData.id) {
    payload.id = formData.id;
  }

  addIfNotEmpty('patientAccountNumber', formData[FormFields.patientAccountNumber]);
  addIfNotEmpty('chiefComplaint', formData[FormFields.chiefComplaint]);
  addIfNotEmpty('modeOfArrivalText', formData[FormFields.modeOfArrivalText]);
  addIfNotEmpty('admitTimestamp', formData[FormFields.admitTimestamp]);
  addIfNotEmpty('dischargedDisposition', formData[FormFields.dischargedDisposition]);
  addIfNotEmpty('dischargeTimestamp', formData[FormFields.dischargeTimestamp]);
  addIfNotEmpty('dischargeLocation', formData[FormFields.dischargeLocation]);
  addIfNotEmpty('billToProviderLastname', formData[FormFields.billToProviderLastname]);
  addIfNotEmpty('billToProviderFirstname', formData[FormFields.billToProviderFirstname]);
  addIfNotEmpty('billtToProviderMiddlename', formData[FormFields.billtToProviderMiddlename]);
  addIfNotEmpty('billToProviderId', formData[FormFields.billToProviderId]);
  addIfNotEmpty('patientLocation', formData[FormFields.patientLocation]);
  addIfNotEmpty('patientClass', formData[FormFields.patientClass]);
  addIfNotEmpty('hospitalService', formData[FormFields.hospitalService]);
  addIfNotEmpty('patientType', formData[FormFields.patientType]);
  addIfNotEmpty('servicingFacility', formData[FormFields.servicingFacility]);
  addIfNotEmpty('accountStatus', formData[FormFields.accountStatus]);

  return payload;
};
